/**
 * Pricing Mappers
 *
 * Maps mdxui PricingProps to various shadcnblocks pricing block props.
 */

import type { PricingProps } from 'mdxui'

// Derive PricingTier type from PricingProps to ensure we use the correct site/props version
type PricingTier = PricingProps['tiers'][number]

// =============================================================================
// Vendor Block Types
// =============================================================================

/** PricingPlans (comparison variant) plan type */
export interface PricingPlansPlan {
  name: string
  badge: string
  monthlyPrice: string
  yearlyPrice: string
  features: string[]
  buttonText: string
  isPopular?: boolean
}

/** PricingPlans (comparison variant) props */
export interface PricingPlansProps {
  title: string
  description: string
  plans: PricingPlansPlan[]
  className?: string
}

/** PricingCompact (simple variant) props */
export interface PricingCompactProps {
  heading: string
  description: string
  price: number | string
  priceSuffix: string
  features: string[][]
  buttonText: string
}

// =============================================================================
// Variant Mapping
// =============================================================================

/**
 * Maps semantic variant names to shadcnblocks component names
 */
export const PRICING_VARIANT_MAP = {
  /** Simple single-tier display */
  simple: 'PricingCompact',
  /** Comparison table with multiple tiers */
  comparison: 'PricingPlans',
  /** Monthly/yearly toggle pricing */
  toggle: 'PricingPlans',
} as const

export type PricingVariant = keyof typeof PRICING_VARIANT_MAP

// =============================================================================
// Price Parsing Utilities
// =============================================================================

/**
 * Parse a price string like "$29/mo" into numeric and suffix parts
 */
function parsePrice(priceStr: string): { numeric: number | null; suffix: string } {
  // Handle non-numeric prices like "Custom" or "Contact"
  if (!/\d/.test(priceStr)) {
    return { numeric: null, suffix: '' }
  }

  // Extract numeric part (handles $29, $29/mo, $29.99/mo, etc.)
  const numericMatch = priceStr.match(/[\d.]+/)
  const numeric = numericMatch ? parseFloat(numericMatch[0]) : null

  // Extract suffix (everything after the number, like "/mo" or "/month")
  const suffixMatch = priceStr.match(/[\d.]+(.*)/)
  const suffix = suffixMatch ? suffixMatch[1].trim() : ''

  return { numeric, suffix }
}

/**
 * Extract just the price number as a string (e.g., "$29/mo" -> "$29")
 */
function extractPriceDisplay(priceStr: string): string {
  // Handle non-numeric prices
  if (!/\d/.test(priceStr)) {
    return priceStr
  }

  const match = priceStr.match(/^\$?[\d.]+/)
  if (!match) return priceStr

  // Ensure we have the $ prefix
  const price = match[0]
  return price.startsWith('$') ? price : `$${price}`
}

/**
 * Calculate yearly price from monthly (assumes 10 months discount)
 */
function calculateYearlyPrice(monthlyPrice: string): string {
  const { numeric } = parsePrice(monthlyPrice)
  if (numeric === null) return monthlyPrice

  const yearly = Math.round(numeric * 10)
  return `$${yearly}`
}

// =============================================================================
// Mappers
// =============================================================================

/**
 * Map mdxui PricingProps to PricingPlansProps (comparison variant)
 */
export function mapToPricingPlansProps(props: Omit<PricingProps, 'variant'>): PricingPlansProps {
  const { title, description, tiers } = props

  return {
    title: title || 'Pricing',
    description: description || '',
    plans: tiers.map((tier: PricingTier) => ({
      name: tier.name,
      badge: tier.name,
      monthlyPrice: extractPriceDisplay(tier.price),
      yearlyPrice: calculateYearlyPrice(tier.price),
      features: tier.features,
      buttonText: tier.callToAction,
      isPopular: tier.highlighted,
    })),
  }
}

/**
 * Map mdxui PricingProps to PricingCompactProps (simple variant)
 *
 * This variant shows a single pricing tier, so we select the most relevant one:
 * - Uses the highlighted tier if available
 * - Otherwise uses the first tier
 */
export function mapToPricingCompactProps(props: Omit<PricingProps, 'variant'>): PricingCompactProps {
  const { title, description, tiers } = props

  // Select the highlighted tier, or fall back to first tier
  const selectedTier = tiers.find((t: PricingTier) => t.highlighted) || tiers[0]

  if (!selectedTier) {
    return {
      heading: title || 'Pricing',
      description: description || '',
      price: 0,
      priceSuffix: '/mo',
      features: [],
      buttonText: 'Get Started',
    }
  }

  const { numeric, suffix } = parsePrice(selectedTier.price)

  // Group features into arrays of 3 for the PricingCompact layout
  const groupedFeatures: string[][] = []
  const chunkSize = 3
  for (let i = 0; i < selectedTier.features.length; i += chunkSize) {
    groupedFeatures.push(selectedTier.features.slice(i, i + chunkSize))
  }

  return {
    heading: title || 'Pricing',
    description: description || '',
    price: numeric !== null ? numeric : selectedTier.price,
    priceSuffix: suffix,
    features: groupedFeatures,
    buttonText: selectedTier.callToAction,
  }
}
